/** @jsxImportSource react */
import { useState } from 'react'
import { Box, Button, Divider, Link, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useQuery } from '@tanstack/react-query'
import { tasksAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import TaskItem from '../tasks/TaskItem'
import TaskDialog from '../tasks/TaskDialog'

const SHOWN_DONE = 3

/** A record's tasks: open ones (soonest first), then the latest few done. */
const RecordTasks = ({ record }) => {
  const tenantId = useTenantKey()
  const [dialog, setDialog] = useState(null) // null | 'new' | task
  const [showAllDone, setShowAllDone] = useState(false)
  const { data, isLoading, error } = useQuery({
    queryKey: [tenantId, 'tasks', 'record', record.id],
    queryFn: () => tasksAPI.list({ recordId: record.id, size: 100 }),
  })
  const tasks = data?.items || []
  const open = tasks.filter((t) => t.status === 'OPEN')
  const done = tasks.filter((t) => t.status === 'DONE')

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Tasks{open.length > 0 && <Typography component="span" color="text.secondary"> · {open.length}</Typography>}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setDialog('new')}>Add task</Button>
      </Box>
      {isLoading && <Typography variant="body2" color="text.secondary">Loading tasks…</Typography>}
      {error && <Typography variant="body2" color="error">{getErrorMessage(error)}</Typography>}
      {!isLoading && !error && tasks.length === 0 && (
        <Typography variant="body2" color="text.secondary">No tasks yet. Add one to get a reminder.</Typography>
      )}
      {open.map((t) => <TaskItem key={t.id} task={t} showRecord={false} onEdit={setDialog} />)}
      {done.length > 0 && (
        <>
          {open.length > 0 && <Divider sx={{ my: 0.5 }} />}
          {(showAllDone ? done : done.slice(0, SHOWN_DONE)).map((t) => <TaskItem key={t.id} task={t} showRecord={false} onEdit={setDialog} />)}
          {done.length > SHOWN_DONE && (
            <Link component="button" type="button" variant="caption" onClick={() => setShowAllDone((v) => !v)}>
              {showAllDone ? 'Show fewer' : `Show all ${done.length} done`}
            </Link>
          )}
        </>
      )}
      {dialog && (
        <TaskDialog open onClose={() => setDialog(null)} task={dialog === 'new' ? null : dialog}
          recordRef={dialog === 'new' ? { id: record.id, title: record.title } : null} />
      )}
    </Box>
  )
}

export default RecordTasks
